import i18next from 'i18next'
import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { z } from 'zod'
import { useLightningReportTypeRules } from '../../../../data/analyze/reportTypeRules.ts'
import { type AssetLocation, useAssetLocations } from '../../../../data/locations.tsx'
import {
	CreateLightningReportOrScheduleRequestCommonFieldsSchema,
	type CreateLightningReportRequest,
	CreateRepeatedLightningReportScheduleRequestSchema,
	useLightningReportDataAvailability,
} from '../../../../data/reports/lightning.ts'
import { UserPreferencesSchema, usePreferences } from '../../../../data/userProfile.ts'
import { type Dayjs, dayjs } from '../../../../dayjs.ts'
import { useDistanceRangeHelpers } from '../../../distance-range-helpers.ts'
import { createUseFormValues } from '../../createUseFormValues.ts'
import { SignalStrengthRangeSelectionSchema } from '../../SignalStrengthSlider.tsx'
import { areWithinSelectionDistance } from '../locationSelection.ts'
import { exceedsMaxDuration, isInsideCoverageZone, isShorterThanMinDuration, maxDurationForArea } from '../reportTypeRules.ts'
import { BaseReportFormSchema, ReportTimeRangeSchema, useBaseReportDefaultValues } from '../schema.ts'

export type LightningReportForm = z.infer<ReturnType<typeof useLightningFormSchema>>
export const useLightningFormValues = createUseFormValues<LightningReportForm>()

export type FaultTime = z.infer<typeof FaultTimeSchema>
const FaultTimeSchema = z.object({
	faultTime: z.iso.datetime({ error: () => i18next.t(($) => $.zod.faultDateTimeRequired) }),
	buffer: z.object({ unit: z.literal(['second', 'minute', 'hour']), amount: z.number().positive() }),
})

export const useLightningFormSchema = () => {
	// Also pre-fetches the availability data, so that it is cached when entering the time range step
	const { data: mergeDataAvailability } = useLightningReportDataAvailability({ dataset: 'MERGE' })
	const { data: nldnDataAvailability } = useLightningReportDataAvailability({ dataset: 'NLDN' })
	const rules = useLightningReportTypeRules()
	const { data: assetLocations } = useAssetLocations()
	const { formatKmInUserPreferenceUnit } = useDistanceRangeHelpers()
	const { t } = useTranslation()
	const schema = useMemo(
		() =>
			BaseReportFormSchema.extend({
				assetVrns: BaseReportFormSchema.shape.assetVrns.max(rules.maxAssets, {
					error: () => t(($) => $.zod.tooManyAssets, { max: rules.maxAssets }),
				}),
				// The v1 API still takes the unit and date format preferences, and expects them as the user has them
				preferences: BaseReportFormSchema.shape.preferences.extend({
					unit: UserPreferencesSchema.shape.unitsDistance,
					dateFormat: UserPreferencesSchema.shape.dateFormat,
				}),
				// Common fields without validation
				...CreateLightningReportOrScheduleRequestCommonFieldsSchema.pick({
					errorEllipseConfidence: true,
					includeStrikeDamagePotential: true,
				}).shape,
				// Common fields where we need to remove nullability
				includeCloudToCloud: CreateLightningReportOrScheduleRequestCommonFieldsSchema.shape.includeCloudToCloud.unwrap(),
				dataset: CreateLightningReportOrScheduleRequestCommonFieldsSchema.shape.dataset.unwrap(),
				// Form-only fields
				signalStrengthRange: SignalStrengthRangeSelectionSchema,
				timing: z.union([
					z.object({
						type: z.literal('ONE_OFF'),
						time: z.union([
							ReportTimeRangeSchema.extend({ type: z.literal('start-end') }),
							z.object({
								type: z.literal('fault-time'),
								...FaultTimeSchema.shape,
							}),
						]),
					}),
					z
						.object({
							type: z.literal('REPEATED'),
							...CreateRepeatedLightningReportScheduleRequestSchema.pick({
								frequency: true,
								dayOfWeek: true,
								startScheduleAt: true,
								stopScheduleAt: true,
							}).shape,
						})
						.refine((value) => value.frequency !== 'WEEKLY' || !!value.dayOfWeek, { path: ['dayOfWeek'] }),
				]),
			})
				.superRefine((input, ctx) => {
					const selectedLocations = assetLocations.filter(({ assetVrn }) => input.assetVrns.includes(assetVrn))
					if (rules.maxAssetDistanceKm !== undefined && !areWithinSelectionDistance(selectedLocations, rules.maxAssetDistanceKm)) {
						ctx.addIssue({
							code: 'custom',
							message: t(($) => $.reports.form.maxDistanceBetweenAssets, {
								distance: formatKmInUserPreferenceUnit(rules.maxAssetDistanceKm, ' ', 0),
							}),
							path: ['assetVrns'],
						})
					}
				})
				.superRefine((input, ctx) => {
					if (input.timing.type !== 'ONE_OFF') return

					const { start, end } = oneOffTimingToTimeRange(input.timing)
					const dataAvailability = input.dataset === 'MERGE' ? mergeDataAvailability : nldnDataAvailability
					const startDate = dayjs(start)
					const endDate = dayjs(end)

					if (dataAvailability) {
						const isFaultTimeEntryInputMode = input.timing.time.type === 'fault-time'
						const addDateOutsideDataAvailabilityError = (field: 'start' | 'end') => {
							if (isFaultTimeEntryInputMode) {
								ctx.addIssue({
									code: 'custom',
									message: t(($) => $.reports.lightning.form.dateAndBufferOutsideDataAvailability),
									path: ['timing', 'time'],
								})
							} else {
								ctx.addIssue({
									code: 'custom',
									message: t(($) => $.reports.form.dateOutsideDataAvailability),
									path: ['timing', 'time', field],
								})
							}
						}

						const isDateOutsideAllowed = (date: Dayjs) => date.isBefore(dataAvailability.start)

						if (isDateOutsideAllowed(startDate)) {
							addDateOutsideDataAvailabilityError('start')
						}

						if (isDateOutsideAllowed(endDate)) {
							addDateOutsideDataAvailabilityError('end')
						}
					}
				})
				.superRefine((input, ctx) => {
					// The geometry is for the selection before this one while it is pending, so it is not judged
					if (input.geometryPending) return
					const zone = rules.coverageZones[input.dataset]
					if (zone && !isInsideCoverageZone(zone, input.geometry)) {
						ctx.addIssue({
							code: 'custom',
							message: t(($) => $.reports.lightning.form.geometryOutsideNldnReprocessedArea),
							path: ['dataset'],
						})
					}
				})
				.superRefine((input, ctx) => {
					if (input.timing.type !== 'ONE_OFF') return
					const { start, end } = oneOffTimingToTimeRange(input.timing)
					if (!start || !end || !dayjs(end).isAfter(start)) return

					if (isShorterThanMinDuration(rules, dayjs.duration(dayjs(end).diff(start)))) {
						ctx.addIssue({
							code: 'custom',
							message: t(($) => $.reports.lightning.form.timeRangeMinDurationInSecondsNotReached, {
								count: dayjs.duration(rules.minDuration).asSeconds(),
							}),
							path: ['timing', 'time'],
						})
					}
				})
				.superRefine((input, ctx) => {
					if (input.geometryPending) return
					const maxDuration = maxDurationForArea(rules, input.geometry)
					if (!maxDuration) return

					const exceeds = (duration: ReturnType<typeof dayjs.duration>) => exceedsMaxDuration(rules, maxDuration, duration)
					const addExceededIssue = (path: (string | number)[]) =>
						ctx.addIssue({
							code: 'custom',
							message: t(($) => $.reports.lightning.form.timeRangeMaxDurationInDaysExceeded, { count: maxDuration.asDays() }),
							path,
						})

					if (input.timing.type === 'ONE_OFF') {
						const { start, end } = oneOffTimingToTimeRange(input.timing)
						if (!start || !end) return
						if (exceeds(dayjs.duration(dayjs(end).diff(start)))) {
							addExceededIssue(['timing', 'time'])
						}
					} else if (input.timing.type === 'REPEATED') {
						if (exceeds(dayjs.duration(rules.scheduleReportDurations[input.timing.frequency]))) {
							addExceededIssue(['timing', 'frequency'])
						}
					}
				}),
		[mergeDataAvailability, nldnDataAvailability, rules, assetLocations, formatKmInUserPreferenceUnit, t],
	)

	return schema
}

export const useLightningDefaultValues = (selectedLocations: AssetLocation[]): LightningReportForm => {
	const { data: preferences } = usePreferences()
	const baseDefaultValues = useBaseReportDefaultValues(selectedLocations)

	return {
		...baseDefaultValues,
		preferences: {
			...baseDefaultValues.preferences,
			dateFormat: preferences?.dateFormat ?? 'DAY_MONTH_YEAR',
			unit: preferences?.unitsDistance ?? 'KILOMETRES',
		},
		timing: { type: 'ONE_OFF', time: { type: 'start-end', start: '', end: '' } },
		dataset: 'MERGE',
		includeStrikeDamagePotential: false,
		includeCloudToCloud: true,
		signalStrengthRange: { min: undefined, max: undefined, inverted: false },
	}
}

export const faultTimeToTimeRange = (faultTime: FaultTime): CreateLightningReportRequest['timeRange'] => {
	if (!faultTime.faultTime) return { start: '', end: '' }

	return {
		start: dayjs(faultTime.faultTime).subtract(faultTime.buffer.amount, faultTime.buffer.unit).toISOString(),
		end: dayjs(faultTime.faultTime).add(faultTime.buffer.amount, faultTime.buffer.unit).toISOString(),
	}
}

type Timing = LightningReportForm['timing']
export type OneOffTiming = Extract<Timing, { type: 'ONE_OFF' }>
export type RepeatedTiming = Extract<Timing, { type: 'REPEATED' }>

export const oneOffTimingToTimeRange = (timing: OneOffTiming) =>
	timing.time.type === 'start-end' ? timing.time : faultTimeToTimeRange(timing.time)

export const useIsFutureOneOffReport = () => {
	const { data: mergeDataAvailability } = useLightningReportDataAvailability({ dataset: 'MERGE' })
	const { data: nldnDataAvailability } = useLightningReportDataAvailability({ dataset: 'NLDN' })

	return (input: LightningReportForm): input is LightningReportForm & { timing: OneOffTiming } => {
		const dataAvailability = input.dataset === 'MERGE' ? mergeDataAvailability : nldnDataAvailability
		return input.timing.type === 'ONE_OFF' && dayjs(oneOffTimingToTimeRange(input.timing).end).isAfter(dataAvailability?.end)
	}
}

export const transformSignalStrengthRanges = (
	signalStrengthRange: LightningReportForm['signalStrengthRange'],
): CreateLightningReportRequest['signalStrengthRanges'] => {
	if (signalStrengthRange.min === undefined && signalStrengthRange.max === undefined) return undefined

	const signalStrengthRanges: CreateLightningReportRequest['signalStrengthRanges'] = []
	if (signalStrengthRange.inverted) {
		if (signalStrengthRange.min !== undefined) signalStrengthRanges.push({ min: undefined, max: signalStrengthRange.min })
		if (signalStrengthRange.max !== undefined) signalStrengthRanges.push({ min: signalStrengthRange.max, max: undefined })
	} else {
		signalStrengthRanges.push({ min: signalStrengthRange.min, max: signalStrengthRange.max })
	}

	return signalStrengthRanges
}
